import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, BatteryWarning, Bell, Package, PackagePlus, Truck } from 'lucide-react';
import goodsPickupOrderService from '@services/goodsPickupOrderService';
import { vehicleTrackerService } from '@services/vehicleTrackerService';
import { usePermissions } from '@hooks/usePermissions';
import { batteryAlertText, formatAgo } from '@pages/fleetTracking/trackerUtils';
import type { TrackerBatteryAlert } from '@/types/vehicleTracker';
import goodsDeliveryOrderService from '@services/goodsDeliveryOrderService';
import dashboardService from '@services/dashboardService';
import { CalendarEvent, DashboardNotifications } from '@/types/dashboard';
import { isoToBR } from '@components/dateInputUtils';
import { eventRefPrefix, eventTypeLabel, routeForEvent } from '@/pages/dashboard/panels/calendarUtils';
import { GoodsDeliveryOrderPendingCount } from '@/types/goodsDeliveryOrder';

/**
 * Sino de notificações do header. Fontes (cada uma falha em silêncio, sem
 * derrubar as outras — notificação é não-crítica):
 *  1. Ordens de retirada pendentes (endpoint próprio, goods_pickup_orders.read);
 *  1b. Ordens de entrega avulsas pendentes/emergenciais (pending-count próprio,
 *     goods_delivery_orders.read; sem permissão → zeros, entrada some);
 *  2. Pedidos prioritários com evento HOJE/AMANHÃ (compra por data prevista,
 *     venda por data necessária/envio agendado) — expande a relação;
 *  3. Recebimentos de material com produto personalizado (visíveis por 2 dias
 *     úteis) para vendas/administrativo conferirem contra a foto do projeto;
 *  4. Rastreadores com bateria baixa ou desligados por bateria (só com
 *     fleet_tracking.read; o item abre a aba Bateria do rastreador).
 * Refetch a cada navegação; somente desktop (mesmo guard do sino original).
 */
export const NotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [pendingPickupCount, setPendingPickupCount] = useState(0);
  const [deliveryCount, setDeliveryCount] = useState<GoodsDeliveryOrderPendingCount>({ pending: 0, emergency: 0 });
  const [notifications, setNotifications] = useState<DashboardNotifications | null>(null);
  const [priorityExpanded, setPriorityExpanded] = useState(false);
  const [batteryAlerts, setBatteryAlerts] = useState<TrackerBatteryAlert[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { checkPermission, isLoading: permissionsLoading } = usePermissions();
  const canSeeFleet = !permissionsLoading && checkPermission('fleet_tracking.read').hasPermission;

  useEffect(() => {
    const isMobile = window.matchMedia('(max-width: 639px)').matches;
    if (isMobile) return;

    const fetchPendingCount = async () => {
      try {
        const count = await goodsPickupOrderService.getPendingCount();
        setPendingPickupCount(count);
      } catch {
        // Silent fail — notification is non-critical
      }
    };
    const fetchNotifications = async () => {
      try {
        const data = await dashboardService.getNotifications();
        setNotifications(data);
      } catch {
        // Silent fail — notification is non-critical
      }
    };
    const fetchDeliveryCount = async () => {
      try {
        setDeliveryCount(await goodsDeliveryOrderService.getPendingCount());
      } catch {
        // Silent fail — notification is non-critical
      }
    };
    fetchPendingCount();
    fetchDeliveryCount();
    fetchNotifications();
  }, [location.pathname]);

  useEffect(() => {
    if (!canSeeFleet || window.matchMedia('(max-width: 639px)').matches) return;
    vehicleTrackerService
      .batteryAlerts()
      .then(setBatteryAlerts)
      .catch(() => {
        // Silent fail — notification is non-critical
      });
  }, [location.pathname, canSeeFleet]);

  const priorityToday = notifications?.priority_orders.today ?? [];
  const priorityTomorrow = notifications?.priority_orders.tomorrow ?? [];
  const priorityCount = priorityToday.length + priorityTomorrow.length;
  const customReceipts = notifications?.custom_material_receipts ?? [];
  const hasAny =
    pendingPickupCount > 0 ||
    deliveryCount.pending > 0 ||
    deliveryCount.emergency > 0 ||
    priorityCount > 0 ||
    customReceipts.length > 0 ||
    batteryAlerts.length > 0;

  const goTo = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const renderPriorityEvent = (ev: CalendarEvent, when: string) => (
    <button
      key={`${when}-${ev.type}-${ev.id}`}
      className="w-full text-left px-3 py-1.5 hover:bg-slate-50 rounded transition-colors"
      onClick={() => goTo(routeForEvent(ev))}
    >
      <p className="text-xs text-slate-700">
        <span className="font-medium">
          {eventRefPrefix(ev.type)} #{ev.id}
        </span>
        {ev.label ? ` · ${ev.label}` : ''}
      </p>
      <p className="text-[11px] text-slate-400">
        {eventTypeLabel(ev.type)} — {when} ({isoToBR(ev.date)})
      </p>
    </button>
  );

  return (
    <div className="relative">
      <button
        className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors relative"
        onClick={() => setOpen(!open)}
      >
        <Bell size={20} />
        {hasAny && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50 max-h-[70vh] overflow-y-auto">
          <div className="px-4 py-3 border-b border-slate-200">
            <p className="text-sm font-semibold text-slate-900">Notificações</p>
          </div>

          {pendingPickupCount > 0 && (
            <button
              className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors"
              onClick={() => goTo('/goods-pickup-orders')}
            >
              <div className="flex items-start gap-3">
                <div className="p-1.5 bg-amber-100 rounded-full mt-0.5">
                  <Truck size={14} className="text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">Ordens de retirada pendentes</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Existem {pendingPickupCount} ordem(ns) de retirada pendente(s)
                  </p>
                </div>
              </div>
            </button>
          )}

          {deliveryCount.emergency > 0 && (
            <button
              className="w-full text-left px-4 py-3 bg-red-50 hover:bg-red-100 transition-colors border-t border-red-100"
              onClick={() => goTo('/goods-delivery-orders?emergency=1')}
            >
              <div className="flex items-start gap-3">
                <div className="p-1.5 bg-red-600 rounded-full mt-0.5">
                  <AlertTriangle size={14} className="text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-red-700">Entregas emergenciais</p>
                  <p className="text-xs text-red-600 mt-0.5">
                    {deliveryCount.emergency} ordem(ns) de entrega EMERGENCIAL(is)
                  </p>
                </div>
              </div>
            </button>
          )}

          {deliveryCount.pending > 0 && (
            <button
              className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors border-t border-slate-100"
              onClick={() => goTo('/goods-delivery-orders')}
            >
              <div className="flex items-start gap-3">
                <div className="p-1.5 bg-teal-100 rounded-full mt-0.5">
                  <PackagePlus size={14} className="text-teal-700" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">Ordens de entrega pendentes</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {deliveryCount.pending} ordem(ns) de entrega pendente(s)
                  </p>
                </div>
              </div>
            </button>
          )}

          {priorityCount > 0 && (
            <div className="border-t border-slate-100">
              <button
                className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors"
                onClick={() => setPriorityExpanded(!priorityExpanded)}
              >
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-red-100 rounded-full mt-0.5">
                    <AlertTriangle size={14} className="text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      Existem pedidos prioritários para acompanhamento
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {priorityToday.length > 0 && `${priorityToday.length} hoje`}
                      {priorityToday.length > 0 && priorityTomorrow.length > 0 && ' · '}
                      {priorityTomorrow.length > 0 && `${priorityTomorrow.length} amanhã`}
                      {' — clique para ver a relação'}
                    </p>
                  </div>
                </div>
              </button>
              {priorityExpanded && (
                <div className="px-3 pb-2 space-y-0.5">
                  {priorityToday.map((ev) => renderPriorityEvent(ev, 'hoje'))}
                  {priorityTomorrow.map((ev) => renderPriorityEvent(ev, 'amanhã'))}
                </div>
              )}
            </div>
          )}

          {customReceipts.map((receipt) => (
            <button
              key={`receipt-${receipt.receipt_id}`}
              className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors border-t border-slate-100"
              onClick={() => goTo(`/material-receipts/${receipt.receipt_id}`)}
            >
              <div className="flex items-start gap-3">
                <div className="p-1.5 bg-blue-100 rounded-full mt-0.5">
                  <Package size={14} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">
                    Recebimento com produto personalizado
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Recebimento #{receipt.receipt_id} em {isoToBR(receipt.receipt_date)} — conferir:{' '}
                    {receipt.product_names || 'produto personalizado'}
                  </p>
                </div>
              </div>
            </button>
          ))}

          {batteryAlerts.map((a) => {
            const severe = a.level !== 'low';
            return (
              <button
                key={`battery-${a.vehicle_tracker_id}`}
                className={`w-full text-left px-4 py-3 transition-colors border-t ${
                  severe ? 'bg-red-50 hover:bg-red-100 border-red-100' : 'hover:bg-slate-50 border-slate-100'
                }`}
                onClick={() => goTo(`/settings/transport/trackers?battery=${a.vehicle_tracker_id}`)}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-1.5 rounded-full mt-0.5 ${severe ? 'bg-red-600' : 'bg-amber-100'}`}>
                    <BatteryWarning size={14} className={severe ? 'text-white' : 'text-amber-600'} />
                  </div>
                  <div>
                    <p className={`text-sm ${severe ? 'font-semibold text-red-700' : 'font-medium text-slate-900'}`}>
                      {batteryAlertText(a.level)} — {a.battery_percent}%
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {a.vehicle_label || `Rastreador ${a.device_id}`} · última comunicação {formatAgo(a.last_seen_seconds_ago)}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}

          {!hasAny && (
            <div className="px-4 py-6 text-center text-sm text-slate-400">Nenhuma notificação</div>
          )}
        </div>
      )}

      {/* Click outside to close */}
      {open && <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />}
    </div>
  );
};
